import React, { useState } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';

import SimpleTrack from '@common/types/release/simple-track';

import Chip from '@components/basics/chip';
import Checkbox from '@components/form/checkbox';

import AnimatedHintCtrl from '@webapp/controllers/animated-hint';
import InstantGratificationIcon from '@components/basics/icons/track/instant-gratification-icon';

interface InstantGratificationCtrlProps {
  className?: string;
  trackId: string;
}

interface StoreProps {
  isPlanFeature: boolean;
  hasPreorder: boolean;
  isInstantGratification: boolean;
  disableFeature: boolean;
  tracksWithInstantGratif: Partial<SimpleTrack>[];
  updateInstantGratification: (
    value: boolean
  ) => UpdateTrackField<'isInstantGratification'>;
}

const mapStateToProps = (
  state: AppState,
  props: InstantGratificationCtrlProps
) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const tracksWithInstantGratif = tracks.filter(
    (item) => item.isInstantGratification
  );
  const condition =
    tracks && tracks.length > 0
      ? Math.floor(tracks.length / 2) > tracksWithInstantGratif.length
      : false;
  const isInstantGratification =
    !!state.tracks.tracks[props.trackId].isInstantGratification;
  return {
    hasPreorder: !!state.release.release?.hasPreorder,
    isInstantGratification: isInstantGratification,
    tracksWithInstantGratif: tracksWithInstantGratif,
    isPlanFeature: !!(
      state.subscription.current?.plan.toUpperCase().includes('PRO') &&
      state.subscription.current.isActive
    ),
    disableFeature: isInstantGratification ? false : !condition,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  props: InstantGratificationCtrlProps
) => ({
  updateInstantGratification: (value: boolean) =>
    dispatch(
      new UpdateTrackField<'isInstantGratification'>(
        props.trackId,
        'isInstantGratification',
        value
      )
    ),
});

export const InstantGratificationCtrl: React.FC<
  InstantGratificationCtrlProps & StoreProps
> = ({
  isPlanFeature,
  disableFeature,
  isInstantGratification,
  trackId,
  tracksWithInstantGratif,
  ...props
}) => {
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState<
    | 'RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.HINT.DISABLE'
    | 'RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.HINT'
  >('RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.HINT');

  React.useEffect(() => {
    setShowHint(false);
  }, [trackId]);

  const handleChange = () => {
    props.updateInstantGratification(!isInstantGratification);
    if (!isInstantGratification && !isPlanFeature) {
      setShowHint(true);
      setHint('RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.HINT');
    }
  };

  const getTracksWithIG = () => {
    const tracks = tracksWithInstantGratif
      .map((track) => track.order)
      .join(', ');
    return ` Tracks ${tracks} already selected.`;
  };

  return props.hasPreorder ? (
    <div className={props.className}>
      <Checkbox
        active={!!isInstantGratification}
        onClick={() => handleChange()}
        titleDecoration={
          <StyledIcon isInvertedTheme disabled={disableFeature} />
        }
        disabled={disableFeature}
        customBorderRadius={
          showHint && !!isInstantGratification ? '4px 4px 0 0' : undefined
        }
        title={
          <Translate id="RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.TITLE" />
        }
        text={
          !isPlanFeature && (
            <Translate id="RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.COPY" />
          )
        }
        hasIcons={
          !isPlanFeature && (
            <Chip chipType="success" hasActive={!!isInstantGratification} small>
              $1
            </Chip>
          )
        }
      />
      {showHint && !!isInstantGratification && (
        <AnimatePresence exitBeforeEnter>
          <AnimatedHintCtrl noTitle align="left" noMargin noBorderTop>
            <Translate id={hint} />
            {hint ===
              'RELEASE.TRACK_INFO.TRACK.INSTANT_GRATIFICATION.HINT.DISABLE' &&
              tracksWithInstantGratif.length > 0 &&
              getTracksWithIG()}
          </AnimatedHintCtrl>
        </AnimatePresence>
      )}
    </div>
  ) : null;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InstantGratificationCtrl);

const StyledIcon = styled(InstantGratificationIcon)`
  margin-left: 5px;
`;
